import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, Mail } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import type { User as UserType, UserRole, CreateUserData } from '@/types';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (userData: CreateUserData) => Promise<void>;
  user?: UserType | null; // Per edit (non implementato in questa fase)
  currentUserRole: UserRole;
}

export const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user = null,
  currentUserRole,
}) => {
  const [formData, setFormData] = useState<CreateUserData>({
    email: '',
    displayName: '',
    role: 'user',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEdit = !!user;
  const isSuperAdmin = currentUserRole === 'superadmin';

  // Reset form quando si apre/chiude il modal
  useEffect(() => {
    if (isOpen) {
      if (user) {
        setFormData({
          email: user.email,
          displayName: user.displayName,
          role: user.role,
        });
      } else {
        setFormData({
          email: '',
          displayName: '',
          role: 'user',
        });
      }
      setErrors({});
    }
  }, [isOpen, user]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email obbligatoria';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email non valida';
    }

    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Nome obbligatorio';
    } else if (formData.displayName.trim().length < 3) {
      newErrors.displayName = 'Nome troppo corto (min 3 caratteri)';
    }

    if (!formData.role) {
      newErrors.role = 'Ruolo obbligatorio';
    }

    // Solo superadmin può creare admin
    if (formData.role === 'admin' && !isSuperAdmin) {
      newErrors.role = 'Solo il superadmin può creare admin';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      await onSubmit(formData);
      onClose();
    } catch (error: any) {
      setErrors({
        submit: error.message || 'Errore durante il salvataggio',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const roleOptions = isSuperAdmin
    ? [
        { value: 'user', label: 'Utente' },
        { value: 'admin', label: 'Admin' },
      ]
    : [{ value: 'user', label: 'Utente' }];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {isEdit ? 'Modifica Utente' : 'Nuovo Utente'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Email */}
            <Input
              label="Email"
              type="email"
              icon={Mail}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={errors.email}
              placeholder="utente@esempio.com"
              disabled={loading || isEdit}
              fullWidth
              required
            />

            {/* Display Name */}
            <Input
              label="Nome Completo"
              type="text"
              icon={User}
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              error={errors.displayName}
              placeholder="Mario Rossi"
              disabled={loading}
              fullWidth
              required
            />

            {/* Role */}
            <Select
              label="Ruolo"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              error={errors.role}
              options={roleOptions}
              disabled={loading || isEdit}
              fullWidth
              required
            />

            {/* Info */}
            {!isEdit && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  ℹ️ L'utente riceverà un'email con un link per completare la registrazione e impostare la password.
                </p>
              </div>
            )}

            {/* Error generale */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-900">{errors.submit}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={loading}
                fullWidth
              >
                Annulla
              </Button>
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                fullWidth
              >
                {isEdit ? 'Salva Modifiche' : 'Crea Utente'}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
